import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { HiveBanner } from "@/components/HiveBanner";
import {
  LayoutDashboard, Dumbbell, LineChart, CalendarDays, Sparkles, Salad, Flower2, Camera, Trophy,
  CreditCard, Receipt, Users, LogOut, Menu, X, ClipboardList, Award, Flame,
  NotebookPen, StretchHorizontal, Target, Swords, CalendarRange, Timer as TimerIcon, Medal, Gauge,
  MessageSquare, UsersRound, ListChecks, MoreHorizontal, CalendarCheck, Building2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { FEATURES } from "@/lib/features";
import { InstallApp } from "@/components/perform/InstallApp";
import { GetTheAppButton } from "@/components/perform/GetTheApp";
import { FeedbackButton } from "@/components/perform/FeedbackVoice";

// Grouped menu: train, measure, squad, wellbeing, coach, account
const GROUPS = [
  { label: "train", items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/today", label: "Today's WOD", icon: Swords },
    { to: "/app/timetable", label: "Timetable & booking", icon: CalendarCheck, clubOnly: true },
    { to: "/app/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/app/areas", label: "Training Areas", icon: Target },
    { to: "/app/workouts", label: "Workouts", icon: Dumbbell },
    { to: "/app/warmup", label: "Warm-up & Stretch", icon: StretchHorizontal },
    { to: "/app/timer", label: "Timer", icon: TimerIcon },
    { to: "/app/train", label: "Train (Live Cam)", icon: Camera },
    ...(FEATURES.padel ? [{ to: "/app/padel", label: "Padel", icon: Trophy }] : []),
  ] },
  { label: "measure", items: [
    { to: "/app/benchmarks", label: "Benchmarks & Maxes", icon: Medal },
    { to: "/app/performance", label: "Fitness Level", icon: Gauge },
    { to: "/app/progress", label: "Progress", icon: LineChart },
    { to: "/app/diary", label: "Training Diary", icon: NotebookPen },
    { to: "/app/leaderboard", label: "Leaderboards", icon: Award },
  ] },
  { label: "squad", items: [
    { to: "/app/community", label: "Squad", icon: UsersRound },
    { to: "/app/messages", label: "Messages", icon: MessageSquare, clubOnly: true, badge: "messages" },
    { to: "/app/feed", label: "PR Feed", icon: Flame },
    { to: "/app/schedule", label: "Classes & Scheduling", icon: CalendarDays, noClub: true },
    { to: "/app/competitions", label: "Competitions", icon: Trophy },
  ] },
  { label: "wellbeing", items: [
    { to: "/app/nutrition", label: "Nutrition", icon: Salad },
    { to: "/app/habits", label: "Habits", icon: ListChecks },
    { to: "/app/mind", label: "Mind & Flow", icon: Flower2 },
  ] },
  { label: "coach", items: [{ to: "/app/coach", label: "OCTON Coach", icon: Sparkles }] },
];
const trainerExtra = { to: "/app/clients", label: "Customers", icon: Users };
const adminInsights = { to: "/app/crm", label: "Business Insights", icon: ClipboardList };
// Phone tab bar: what each kind of member reaches for most
const TABS = [
  { to: "/app/today", label: "WOD", icon: Swords },
  { to: "/app/calendar", label: "Calendar", icon: CalendarRange },
  { to: "/app/coach", label: "Coach", icon: Sparkles },
  { to: "/app/community", label: "Squad", icon: UsersRound },
];
const CLUB_TABS = [
  { to: "/app/today", label: "WOD", icon: Swords },
  { to: "/app/timetable", label: "Book", icon: CalendarCheck },
  { to: "/app/calendar", label: "Calendar", icon: CalendarRange },
  { to: "/app/coach", label: "Coach", icon: Sparkles },
];
const STAFF_TABS = [
  { to: "/app/club", label: "Club", icon: Building2 },
  { to: "/app/timetable", label: "Timetable", icon: CalendarCheck },
  { to: "/app/programming", label: "Program", icon: ClipboardList },
  { to: "/app/today", label: "WOD", icon: Swords },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Staff-only pages sit just below Memberships: Customers (trainers and admin), Business Insights (admin only)
  const isStaff = user?.role === "trainer" || user?.role === "admin";
  const clubStaff = ["owner", "coach"].includes(user?.club_role);
  const inClub = !!(user?.club_id && user?.club_active);
  // Owners and coaches run the gym first and train second; members see their training first
  const staffGroup = { label: "run your club", items: [
    { to: "/app/club", label: "Club home", icon: Building2 },
    { to: "/app/timetable", label: "Timetable & bookings", icon: CalendarCheck },
    { to: "/app/programming", label: "Programming", icon: ClipboardList },
    { to: "/app/messages", label: "Messages", icon: MessageSquare, badge: "messages" },
    { to: "/app/community", label: "News & challenges", icon: UsersRound },
  ] };
  const memberGroups = GROUPS.map((g) => ({ ...g,
    label: clubStaff && g.label === "train" ? "my own training" : g.label,
    items: g.items.filter((i) => (!i.clubOnly || inClub) && !(i.noClub && inClub) && !(clubStaff && ["/app/timetable", "/app/messages", "/app/community"].includes(i.to))) }));
  const groups = [
    ...(clubStaff ? [staffGroup] : []),
    ...memberGroups,
    { label: "account", items: [
      { to: "/app/membership", label: "Memberships", icon: CreditCard },
      ...(isStaff ? [trainerExtra] : []), ...(user?.role === "admin" ? [adminInsights] : []),
      { to: "/app/payments", label: "Payments", icon: Receipt },
    ] },
  ];
  const { data: unread } = useQuery({ queryKey: ["unread"], enabled: !!user?.club_id, refetchInterval: 30000,
    queryFn: async () => (await api.get("/api/messages-unread")).data.unread });

  const doLogout = async () => { await logout(); navigate("/login"); };
  const location = useLocation();
  useEffect(() => {
    // Owners and coaches open the app on their club home (once per visit, so "My own training" still works)
    let seen = false;
    try { seen = sessionStorage.getItem("octon_home") === "1"; sessionStorage.setItem("octon_home", "1"); } catch (_) {}
    if (clubStaff && !seen && (location.pathname === "/app" || location.pathname === "/app/today")) navigate("/app/club", { replace: true });
  }, [clubStaff]); // eslint-disable-line
  useEffect(() => {
    // anonymous usage counts for Business Insights (which features members actually open)
    api.post("/api/track", { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#050505]">
      {/* Sidebar */}
      <aside className={`fixed z-40 md:static inset-y-0 left-0 w-64 surface border-r border-[#1E222A] flex flex-col
        transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <button onClick={() => navigate("/")} data-testid="sidebar-logo-home"
          className="p-5 flex items-center gap-2.5 border-b border-[#1E222A] hover:bg-white/5 transition-colors text-left">
          <OctonLogo size={34} />
          <div className="leading-none">
            <p className="font-heading font-black tracking-tight">OCTON GYM</p>
            <p className="mono-label mt-1">AI personal coach</p>
          </div>
        </button>
        <nav className="flex-1 p-3 overflow-y-auto" data-testid="side-nav">
          {groups.map((g) => (
            <div key={g.label} className="mb-3">
              <p className="mono-label px-3 mb-1 text-[#565C6F]">{g.label}</p>
              <div className="space-y-0.5">
                {g.items.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        isActive ? "bg-acid/10 text-acid" : "text-[#8A91A6] hover:text-white hover:bg-white/5"
                      }`}>
                    <n.icon className="w-4 h-4" /> <span className="flex-1">{n.label}</span>
                    {n.badge === "messages" && unread > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-acid text-black text-xs font-bold flex items-center justify-center">{unread}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1E222A]">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-9 h-9 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm truncate">{user?.name}</p>
              <p className="mono-label truncate">{roleLabel(user)}</p>
            </div>
          </div>
          <FeedbackButton label="Send feedback to OCTON" testid="feedback-btn-side" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#D9BCFF] hover:bg-[#B57BFF]/10 transition-colors" />
          <GetTheAppButton label="Get the app on your phone" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-acid hover:bg-acid/10 transition-colors" />
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#8A91A6] hover:text-pulseError hover:bg-white/5 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        {user?.role === "admin" && <HiveBanner />}
        <header className="md:hidden glass sticky top-0 z-20 flex items-center justify-between px-4 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <button onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="mobile-logo-home">
            <OctonLogo size={26} />
            <span className="font-heading font-black">OCTON GYM</span>
          </button>
          <FeedbackButton testid="feedback-btn-top" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#B57BFF]/15 border border-[#B57BFF]/50 text-[#D9BCFF] text-xs font-medium" />
        </header>
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0">{children}</main>
        <InstallApp />
        {/* phone tab bar: the app's main places one thumb away */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-[#1E222A] grid grid-cols-5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="tab-bar">
          {(clubStaff ? STAFF_TABS : inClub ? CLUB_TABS : TABS).map((t) => (
            <NavLink key={t.to} to={t.to} className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${isActive ? "text-acid" : "text-[#8A91A6]"}`}>
              <t.icon className="w-5 h-5" /> {t.label}
            </NavLink>
          ))}
          <button onClick={() => setOpen(true)} className="flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] text-[#8A91A6]" data-testid="tab-more">
            <MoreHorizontal className="w-5 h-5" /> More
          </button>
        </nav>
      </div>
    </div>
  );
}

function roleLabel(u) {
  if (!u) return "";
  if (u.role === "admin") return "Admin";
  if (u.role === "trainer") return "Coach";
  if (u.club_role === "owner") return "Club owner";
  if (u.club_role === "coach") return "Club coach";
  if (u.club_id) return u.club_name ? `${u.club_name} athlete` : "Club athlete";
  return "Member";
}
